import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  signal,
  AfterViewInit,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TagModule } from 'primeng/tag';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePickerModule } from 'primeng/datepicker';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { InputTextModule } from 'primeng/inputtext';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { ButtonModule } from 'primeng/button';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { Subject, of, Observable, firstValueFrom } from 'rxjs';
import { debounceTime, takeUntil, switchMap, catchError, tap } from 'rxjs/operators';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TicketService } from '../../../core/services/ticket.service';
import { TicketAutomationService } from '../../../core/automations/ticket-automation.service';
import { CustomerDetailModalComponent } from '../../customer-detail-modal/customer-detail-modal.component';
import { TicketDetail } from '../../../core/models/ticket.model';
import { TcDataService } from '../../../../../shared/services/ticket-center/tc-data.service';
import { ChipsComponent } from '@teorema/patron';
import { AutoEditFocusDirective } from '../../../core/directives/auto-edit-focus.directive';
import { constants } from '@app/shared/utils/constants';
import { FavoriteListKey } from '../../../core/models/favorite-list.model';
import {
  EntityPickerComponent,
  EntityPickerSearchParams,
  EntityPickerResult,
} from '../../entity-picker';

@Component({
  selector: 'app-ticket-info-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TagModule,
    CheckboxModule,
    TooltipModule,
    InputNumberModule,
    DatePickerModule,
    ToggleSwitchModule,
    InputTextModule,
    SelectButtonModule,
    SelectModule,
    MultiSelectModule,
    ButtonModule,
    ChipsComponent,
    NgxMaskDirective,
    NgxMaskPipe,
    CustomerDetailModalComponent,
    ConfirmDialogModule,
    AutoEditFocusDirective,
    EntityPickerComponent,
  ],
  providers: [provideNgxMask(), ConfirmationService],
  templateUrl: './ticket-info-panel.component.html',
  styleUrls: ['./ticket-info-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketInfoPanelComponent implements AfterViewInit, OnInit, OnDestroy {
  /** Enum das listas de favoritos, para uso no template. */
  protected readonly FavoriteKey = FavoriteListKey;

  private readonly ticketService = inject(TicketService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dataService = inject(TcDataService);
  private readonly automation = inject(TicketAutomationService);
  private readonly projectSearchSubject = new Subject<string>();
  private readonly phaseSearchSubject = new Subject<string>();
  private readonly sprintSearchSubject = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  @Input({ required: true }) ticket!: TicketDetail;
  @Input() canEdit = true;
  @Output() ticketUpdated = new EventEmitter<TicketDetail>();
  /**
   * Emitido ao entrar/saír do modo de edição em lote. Os componentes pai
   * (`ticket-detail`/`ticket-detail-modal`) usam isso só para logar quando um
   * merge de socket chega com o lote aberto — visibilidade para depuração,
   * sem bloquear nem revalidar o payload antes de salvar.
   */
  @Output() bulkEditModeChanged = new EventEmitter<boolean>();

  private readonly sectionExpandedKey = 'ticketInfoPanelSections';

  editingField = signal<string | null>(null);
  saving = signal(false);
  customerDetailModalVisible = signal(false);
  customerIdForModal = signal<string | null>(null);
  sectionExpanded = signal(this.loadSectionExpanded());

  /** Modo de edição em lote ("Modo Edição"): todos os campos ficam editáveis ao mesmo tempo. */
  bulkEditMode = signal(false);
  /** Snapshot dos valores `edit*` no momento em que o lote abriu — base do diff do "Aplicar". */
  private bulkEditSnapshot: Record<string, unknown> = {};
  /**
   * Campos que participam do modo de edição em lote. Deliberadamente NÃO
   * inclui `urgent` — esse campo é editado só pelo toggle imediato
   * (`toggleUrgent`), fora do fluxo `editingField`, mesmo que `startEdit`/
   * `persistField` também tenham um case para ele (não acionado pelo template).
   */
  private readonly bulkEditFields: readonly string[] = [
    'customer_id',
    'system_id',
    'requester',
    'priority',
    'ticket_status_id',
    'responsible_id',
    'developer_id',
    'executor_id',
    'participant_ids',
    'estimated_effort',
    'commercial_estimated_effort',
    'analysis_expected_date',
    'development_expected_date',
    'expected_date',
    'ticket_type_id',
    'type',
    'issue_id',
    'email',
    'phone',
    'ticket_tags',
    'project_id',
    'phase_id',
    'sprint_id',
  ];

  toggleSection(section: 'principais' | 'adicionais' | 'desenvolvimento'): void {
    this.sectionExpanded.update(s => ({ ...s, [section]: !s[section] }));
    this.persistSectionExpanded(this.sectionExpanded());
  }

  private loadSectionExpanded(): {
    principais: boolean;
    adicionais: boolean;
    desenvolvimento: boolean;
  } {
    const defaults = { principais: true, adicionais: false, desenvolvimento: false };
    if (typeof window === 'undefined') return defaults;
    try {
      const stored = window.localStorage.getItem(this.sectionExpandedKey);
      if (!stored) return defaults;
      const parsed = JSON.parse(stored);
      return {
        principais: parsed.principais ?? defaults.principais,
        adicionais: parsed.adicionais ?? defaults.adicionais,
        desenvolvimento: parsed.desenvolvimento ?? defaults.desenvolvimento,
      };
    } catch {
      return defaults;
    }
  }

  private persistSectionExpanded(state: {
    principais: boolean;
    adicionais: boolean;
    desenvolvimento: boolean;
  }): void {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(this.sectionExpandedKey, JSON.stringify(state));
  }
  shouldRecalculateTaskEffort = signal<boolean>(this.dataService.shouldRecalculateTaskEffort);

  onShouldRecalculateChange(shouldRecalculate: boolean): void {
    this.shouldRecalculateTaskEffort.set(shouldRecalculate);
    this.dataService.shouldRecalculateTaskEffort = shouldRecalculate;
  }

  editPriority = 0;
  editExpectedDate: Date | null = null;
  editUrgent = false;
  editResponsibleName = '';
  editDeveloperName = '';
  editStatusDescription = '';
  editTags: string[] = [];
  editParticipantIds: string[] = [];

  /**
   * `customersList`/`systemsList`/`usersList`/`statusesList`/`ticketTypesList`
   * abaixo só existem hoje porque o método morto `applyLocal()` (código já
   * morto antes desta entrega, fora de escopo remover aqui) ainda os lê —
   * nada mais os popula: os 9 campos que os usavam migraram para
   * `app-entity-picker`, que resolve sua própria busca internamente.
   */
  customersList = signal<any[]>([]);
  /** `protected`: usado direto no template pelo `[value]` do `app-entity-picker`. */
  protected currentCustomerSeed: any = null;

  systemsList = signal<any[]>([]);
  protected currentSystemSeed: any = null;

  usersList = signal<any[]>([]);
  protected currentUserSeeds = new Map<string, any>();

  statusesList = signal<any[]>([]);
  protected currentStatusSeed: any = null;

  ticketTypesList = signal<any[]>([]);
  protected currentTicketTypeSeed: any = null;

  projectsList = signal<any[]>([]);
  projectsLoading = false;
  private currentProjectSeed: any = null;

  phasesList = signal<any[]>([]);
  phasesLoading = false;
  private currentPhaseSeed: any = null;

  sprintsList = signal<any[]>([]);
  sprintsLoading = false;
  private currentSprintSeed: any = null;

  issuesList = signal<any[]>([]);
  protected currentIssueSeed: any = null;

  ngAfterViewInit() {
    this.editUrgent = this.ticket.urgent === 1;
  }

  ngOnInit() {
    this.setupSearch(
      this.projectSearchSubject,
      q => this.ticketService.searchProjects(q),
      this.projectsList,
      'projectsLoading',
      items => {
        if (
          this.currentProjectSeed &&
          !items.some((p: any) => p.id === this.currentProjectSeed.id)
        ) {
          items.unshift(this.currentProjectSeed);
        }
      }
    );

    this.setupSearch(
      this.phaseSearchSubject,
      q => this.ticketService.searchPhases(q),
      this.phasesList,
      'phasesLoading',
      items => {
        if (this.currentPhaseSeed && !items.some((p: any) => p.id === this.currentPhaseSeed.id)) {
          items.unshift(this.currentPhaseSeed);
        }
      }
    );

    this.setupSearch(
      this.sprintSearchSubject,
      q => this.ticketService.searchSprints(q),
      this.sprintsList,
      'sprintsLoading',
      items => {
        if (this.currentSprintSeed && !items.some((s: any) => s.id === this.currentSprintSeed.id)) {
          items.unshift(this.currentSprintSeed);
        }
      }
    );
  }

  private setupSearch<T>(
    subject: Subject<string>,
    searchFn: (query: string) => Observable<T[]>,
    listSignal: any,
    loadingProp: 'projectsLoading' | 'phasesLoading' | 'sprintsLoading',
    seedCheckFn: (items: T[]) => void
  ): void {
    subject
      .pipe(
        debounceTime(400),
        tap(() => {
          this[loadingProp] = true;
          this.cdr.markForCheck();
        }),
        switchMap(query => searchFn(query).pipe(catchError(() => of([] as T[])))),
        takeUntil(this.destroy$)
      )
      .subscribe(items => {
        seedCheckFn(items);
        listSignal.set(items);
        this[loadingProp] = false;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onProjectFilter(event: any): void {
    this.projectSearchSubject.next(event.filter || '');
  }

  onPhaseFilter(event: any): void {
    this.phaseSearchSubject.next(event.filter || '');
  }

  onSprintFilter(event: any): void {
    this.sprintSearchSubject.next(event.filter || '');
  }

  editCustomerId = '';
  editSystemId = '';
  editRequester = '';
  editResponsibleId = '';
  editStatusId = '';
  editDeveloperId = '';
  editEmail = '';
  editPhone = '';
  editAnalysisExpectedDate: Date | null = null;
  editDevelopmentExpectedDate: Date | null = null;
  editEstimatedEffort: number | null = null;
  editCommercialEstimatedEffort: number | null = null;
  editType = '';
  editTicketTypeId = '';
  editProjectId = '';
  editPhaseId = '';
  editSprintId = '';
  editExecutorId = '';
  editIssueId = '';

  customers = signal<{ id: string; name: string }[]>([]);
  systems = signal<{ id: string; description: string }[]>([]);
  users = signal<{ id: string; name: string }[]>([]);
  statuses = signal<{ id: string; description: string }[]>([]);
  ticketTypes = signal<{ id: string; description: string }[]>([]);

  priorityOptions = [
    { label: '0 (Maior Prioridade)', value: 0 },
    { label: '1', value: 1 },
    { label: '2', value: 2 },
    { label: '3', value: 3 },
    { label: '4 (Prioridade Média)', value: 4 },
    { label: '5', value: 5 },
    { label: '6', value: 6 },
    { label: '7', value: 7 },
    { label: '8', value: 8 },
    { label: '9 (Menor Prioridade)', value: 9 },
  ];

  ticketsTypes = constants.ticketcenter.ticketsTypes;

  openCustomerDetailModal(): void {
    const customerId = this.ticket?.customer?.id;
    if (customerId) {
      this.customerIdForModal.set(customerId);
      this.customerDetailModalVisible.set(true);
    }
  }

  startEdit(field: string): void {
    if (!this.canEdit) return;
    this.editingField.set(field);
    this.seedField(field);
  }

  /**
   * Popula as variáveis `edit*` do campo a partir do `ticket` atual —
   * exatamente o que `startEdit` fazia inline. Extraído para ser reaproveitado
   * também pelo modo de edição em lote (`enterBulkEditMode`), que precisa
   * semear todos os campos de uma vez.
   */
  private seedField(field: string): void {
    switch (field) {
      case 'priority':
        this.seedPriority();
        break;
      case 'expected_date':
        this.seedExpectedDate();
        break;
      case 'urgent':
        this.seedUrgent();
        break;
      case 'customer_id':
        this.seedCustomerId();
        break;
      case 'system_id':
        this.seedSystemId();
        break;
      case 'requester':
        this.seedRequester();
        break;
      case 'responsible_id':
        this.seedResponsibleId();
        break;
      case 'ticket_status_id':
        this.seedTicketStatusId();
        break;
      case 'developer_id':
        this.seedDeveloperId();
        break;
      case 'estimated_effort':
        this.seedEstimatedEffort();
        break;
      case 'commercial_estimated_effort':
        this.seedCommercialEstimatedEffort();
        break;
      case 'analysis_expected_date':
        this.seedAnalysisExpectedDate();
        break;
      case 'development_expected_date':
        this.seedDevelopmentExpectedDate();
        break;
      case 'type':
        this.seedType();
        break;
      case 'ticket_type_id':
        this.seedTicketTypeId();
        break;
      case 'email':
        this.seedEmail();
        break;
      case 'phone':
        this.seedPhone();
        break;
      case 'ticket_tags':
        this.seedTicketTags();
        break;
      case 'project_id':
        this.seedProjectId();
        break;
      case 'phase_id':
        this.seedPhaseId();
        break;
      case 'sprint_id':
        this.seedSprintId();
        break;
      case 'issue_id':
        this.seedIssueId();
        break;
      case 'executor_id':
        this.seedExecutorId();
        break;
      case 'participant_ids':
        this.seedParticipantIds();
        break;
    }
  }

  private seedPriority(): void {
    this.editPriority = this.ticket.priority;
  }

  private seedExpectedDate(): void {
    this.editExpectedDate = this.parseDate(this.ticket.expected_date);
  }

  private seedUrgent(): void {
    this.editUrgent = this.ticket.urgent === 1;
  }

  private seedCustomerId(): void {
    const custId = (this.ticket.customer as { id?: string })?.id || '';
    this.editCustomerId = custId;
    if (custId && this.ticket.customer_name) {
      const displayName = this.ticket.customer_code
        ? `${this.ticket.customer_code} - ${this.ticket.customer_name}`
        : this.ticket.customer_name;
      this.currentCustomerSeed = {
        id: custId,
        name: this.ticket.customer_name,
        _name: displayName,
      };
      this.customersList.set([this.currentCustomerSeed]);
    }
  }

  private seedSystemId(): void {
    const sysId = this.ticket.system?.id || '';
    this.editSystemId = sysId;
    if (sysId && this.ticket.system?.description) {
      this.currentSystemSeed = {
        id: sysId,
        description: this.ticket.system.description,
      };
      this.systemsList.set([this.currentSystemSeed]);
    }
  }

  private seedRequester(): void {
    this.editRequester = this.ticket.requester || '';
  }

  private seedResponsibleId(): void {
    const respId = this.ticket.responsible?.id || '';
    this.editResponsibleId = respId;
    if (respId && this.ticket.responsible?.name) {
      this.currentUserSeeds.set(respId, {
        id: respId,
        name: this.ticket.responsible.name,
        _name: this.ticket.responsible.name,
      });
      this.usersList.set([...this.currentUserSeeds.values()]);
    }
  }

  private seedTicketStatusId(): void {
    const statId = this.ticket.status?.id || '';
    this.editStatusId = statId;
    if (statId && this.ticket.status?.description) {
      this.currentStatusSeed = {
        id: statId,
        description: this.ticket.status.description,
      };
      this.statusesList.set([this.currentStatusSeed]);
    }
  }

  private seedDeveloperId(): void {
    const devId = this.ticket.developer?.id || '';
    this.editDeveloperId = devId;
    if (devId && this.ticket.developer?.name) {
      this.currentUserSeeds.set(devId, {
        id: devId,
        name: this.ticket.developer.name,
        _name: this.ticket.developer.name,
      });
      this.usersList.set([...this.currentUserSeeds.values()]);
    }
  }

  private seedEstimatedEffort(): void {
    this.editEstimatedEffort = this.ticket.estimated_effort;
  }

  private seedCommercialEstimatedEffort(): void {
    this.editCommercialEstimatedEffort = this.ticket.commercial_estimated_effort;
  }

  private seedAnalysisExpectedDate(): void {
    this.editAnalysisExpectedDate = this.parseDate(this.ticket.analysis_expected_date);
  }

  private seedDevelopmentExpectedDate(): void {
    this.editDevelopmentExpectedDate = this.parseDate(this.ticket.development_expected_date);
  }

  private seedType(): void {
    this.editType = this.ticket.type || '';
  }

  private seedTicketTypeId(): void {
    const typeId = this.ticket.ticket_type?.id || '';
    this.editTicketTypeId = typeId;
    if (typeId && this.ticket.ticket_type?.description) {
      this.currentTicketTypeSeed = {
        id: typeId,
        description: this.ticket.ticket_type.description,
      };
      this.ticketTypesList.set([this.currentTicketTypeSeed]);
    }
  }

  private seedEmail(): void {
    this.editEmail = this.ticket.email || '';
  }

  private seedPhone(): void {
    this.editPhone = this.stripCountryCode(this.ticket.phone || '');
  }

  private seedTicketTags(): void {
    this.editTags = (this.ticket.ticket_tags || []).map(tt => tt.tag.name);
  }

  private seedProjectId(): void {
    const projId = this.ticket.project?.id || '';
    this.editProjectId = projId;
    if (projId && this.ticket.project?.description) {
      this.currentProjectSeed = { id: projId, description: this.ticket.project.description };
      this.projectsList.set([this.currentProjectSeed]);
    }
    this.projectSearchSubject.next('');
  }

  private seedPhaseId(): void {
    const phaseId = this.ticket.phase?.id || '';
    this.editPhaseId = phaseId;
    if (phaseId && this.ticket.phase?.description) {
      this.currentPhaseSeed = { id: phaseId, description: this.ticket.phase.description };
      this.phasesList.set([this.currentPhaseSeed]);
    }
    this.phaseSearchSubject.next('');
  }

  private seedSprintId(): void {
    const sprintId = this.ticket.sprint?.id || '';
    this.editSprintId = sprintId;
    if (sprintId && this.ticket.sprint?.description) {
      this.currentSprintSeed = { id: sprintId, description: this.ticket.sprint.description };
      this.sprintsList.set([this.currentSprintSeed]);
    }
    this.sprintSearchSubject.next('');
  }

  private seedIssueId(): void {
    const issueId = this.ticket.issue?.id || '';
    this.editIssueId = issueId;
    if (issueId && this.ticket.issue?.description) {
      this.currentIssueSeed = {
        id: issueId,
        ref: this.ticket.issue.ref,
        description: this.ticket.issue.description,
        _description:
          (this.ticket.issue.ref ? this.ticket.issue.ref + ' - ' : '') +
          this.ticket.issue.description,
      };
      this.issuesList.set([this.currentIssueSeed]);
    }
  }

  private seedExecutorId(): void {
    const execId = this.ticket.executor?.id || '';
    this.editExecutorId = execId;
    if (execId && this.ticket.executor?.name) {
      this.currentUserSeeds.set(execId, {
        id: execId,
        name: this.ticket.executor.name,
        _name: this.ticket.executor.name,
      });
      this.usersList.set([...this.currentUserSeeds.values()]);
    }
  }

  private seedParticipantIds(): void {
    this.editParticipantIds = (this.ticket.ticketUsers || [])
      .filter(tu => tu.user_type === 'P')
      .map(tu => tu.user?.id)
      .filter((id): id is string => !!id);

    (this.ticket.ticketUsers || [])
      .filter(tu => tu.user_type === 'P')
      .forEach(tu => {
        if (tu.user?.id) {
          this.currentUserSeeds.set(tu.user.id, {
            id: tu.user.id,
            name: tu.user.name,
            _name: tu.user.name,
          });
        }
      });

    this.usersList.set([...this.currentUserSeeds.values()]);
  }

  cancelEdit(): void {
    this.editingField.set(null);
  }

  /**
   * Wrappers usados pelos campos cujo Enter/Escape fica no próprio
   * input/controle, fora do `<div class="edit-actions">` (Solicitante,
   * Esforço Técnico, Esforço Comercial, Email, Telefone) — em modo lote esses
   * atalhos não devem disparar salvamento/cancelamento individual do campo.
   */
  onFieldEnterKey(field: string): void {
    if (this.bulkEditMode()) return;
    this.saveField(field);
  }

  onFieldEscapeKey(): void {
    if (this.bulkEditMode()) return;
    this.cancelEdit();
  }

  getTypeLabel(value: string | undefined): string {
    if (!value) return '—';
    const type = this.ticketsTypes.find((t: any) => t.value === value);
    return type ? type.label : value;
  }

  private formatDateForAPI(date: Date | null): string | null {
    if (!date) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private parseDate(dateStr: string | null | undefined): Date | null {
    if (!dateStr) return null;
    return new Date(dateStr.length === 10 ? dateStr + 'T00:00:00' : dateStr);
  }

  private readonly requiredFields = new Set([
    'customer_id',
    'system_id',
    'requester',
    'responsible_id',
    'ticket_status_id',
  ]);

  /**
   * Valor de edição atual de um campo. Cobre os 23 campos do modo de edição em
   * lote (`bulkEditFields`) — usado tanto na validação de obrigatórios (campo
   * único e em lote) quanto no cálculo do diff do lote.
   */
  private getEditValue(field: string): unknown {
    switch (field) {
      case 'customer_id':
        return this.editCustomerId;
      case 'system_id':
        return this.editSystemId;
      case 'requester':
        return this.editRequester;
      case 'priority':
        return this.editPriority;
      case 'ticket_status_id':
        return this.editStatusId;
      case 'responsible_id':
        return this.editResponsibleId;
      case 'developer_id':
        return this.editDeveloperId;
      case 'executor_id':
        return this.editExecutorId;
      case 'participant_ids':
        return this.editParticipantIds;
      case 'estimated_effort':
        return this.editEstimatedEffort;
      case 'commercial_estimated_effort':
        return this.editCommercialEstimatedEffort;
      case 'analysis_expected_date':
        return this.editAnalysisExpectedDate;
      case 'development_expected_date':
        return this.editDevelopmentExpectedDate;
      case 'expected_date':
        return this.editExpectedDate;
      case 'ticket_type_id':
        return this.editTicketTypeId;
      case 'type':
        return this.editType;
      case 'issue_id':
        return this.editIssueId;
      case 'email':
        return this.editEmail;
      case 'phone':
        return this.editPhone;
      case 'ticket_tags':
        return this.editTags;
      case 'project_id':
        return this.editProjectId;
      case 'phase_id':
        return this.editPhaseId;
      case 'sprint_id':
        return this.editSprintId;
      default:
        return null;
    }
  }

  saveField(field: string): void {
    if (field === 'type' && this.ticket.type === 'L' && this.editType !== 'L') {
      this.confirmationService.confirm({
        header: 'Remover Liberação Pendente',
        message:
          'Tem certeza que deseja remover o tipo "Liberação pendente" deste chamado? ' +
          'Isso irá liberar o chamado para ser finalizado sem o aval do responsável.',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Sim, Remover',
        rejectLabel: 'Cancelar',
        acceptButtonStyleClass: 'p-button-danger',
        accept: () => this.persistField(field),
      });
      return;
    }
    this.persistField(field);
  }

  private persistField(field: string): void {
    if (this.requiredFields.has(field)) {
      const value = this.getEditValue(field);
      if (!value || (typeof value === 'string' && !value.trim())) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Campo obrigatório',
          detail: 'Este campo não pode ficar vazio',
          life: 3000,
        });
        return;
      }
    }

    const payload = this.buildFieldPayload(field);

    // Snapshot ANTES do PATCH: o `Object.assign` abaixo muta `this.ticket`, então depois dele
    // não há mais como saber o que mudou. Ver `TicketAutomationService.snapshot`.
    const automationBefore = this.automation.snapshot(this.ticket);

    this.saving.set(true);
    this.ticketService.patchTicket(this.ticket.id, payload).subscribe({
      next: updatedModel => {
        Object.assign(this.ticket, updatedModel);
        this.ticketUpdated.emit(this.ticket);
        this.saving.set(false);
        this.editingField.set(null);
        this.cdr.markForCheck();

        this.messageService.add({
          severity: 'success',
          summary: 'Salvo',
          detail: 'Campo atualizado com sucesso',
          life: 2000,
        });

        // Por último e fire-and-forget: roda em microtask, nunca lança, nada aqui depende dele.
        this.automation.evaluate(automationBefore, this.ticket);
      },
      error: () => {
        this.saving.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao salvar campo',
          life: 4000,
        });
      },
    });
  }

  /**
   * Monta o payload de PATCH de um único campo — extraído do switch que
   * `persistField` usava inline, para ser reaproveitado também pelo modo de
   * edição em lote (que combina o payload de vários campos alterados num só
   * PATCH). Os nomes de payload que fogem do nome do campo (`executor_id` →
   * `executor`, `participant_ids` → `participants`) são preservados como
   * estavam.
   */
  private buildFieldPayload(field: string): Record<string, unknown> {
    switch (field) {
      case 'priority':
        return { priority: this.editPriority };
      case 'expected_date':
        return { expected_date: this.formatDateForAPI(this.editExpectedDate) };
      case 'urgent':
        return { urgent: this.editUrgent };
      case 'customer_id':
        return { customer_id: this.editCustomerId };
      case 'system_id':
        return { system_id: this.editSystemId };
      case 'requester':
        return { requester: this.editRequester };
      case 'responsible_id':
        return { responsible_id: this.editResponsibleId };
      case 'ticket_status_id':
        return { ticket_status_id: this.editStatusId };
      case 'developer_id':
        return { developer_id: this.editDeveloperId || null };
      case 'estimated_effort':
        return { estimated_effort: this.editEstimatedEffort };
      case 'commercial_estimated_effort':
        return { commercial_estimated_effort: this.editCommercialEstimatedEffort };
      case 'project_id':
        return { project_id: this.editProjectId || null };
      case 'phase_id':
        return { phase_id: this.editPhaseId || null };
      case 'sprint_id':
        return { sprint_id: this.editSprintId || null };
      case 'executor_id':
        return { executor: this.editExecutorId || null };
      case 'issue_id':
        return { issue_id: this.editIssueId || null };
      case 'analysis_expected_date':
        return { analysis_expected_date: this.formatDateForAPI(this.editAnalysisExpectedDate) };
      case 'development_expected_date':
        return {
          development_expected_date: this.formatDateForAPI(this.editDevelopmentExpectedDate),
        };
      case 'type':
        return { type: this.editType };
      case 'ticket_type_id':
        return { ticket_type_id: this.editTicketTypeId };
      case 'email':
        return { email: this.editEmail };
      case 'phone':
        return { phone: this.editPhone };
      case 'ticket_tags':
        return { ticket_tags: this.editTags };
      case 'participant_ids': {
        const participantsData = this.editParticipantIds.map(id => {
          const seed = this.currentUserSeeds.get(id);
          const user = seed ?? this.usersList().find(u => u.id === id);
          if (!user) {
            // Não deveria acontecer — toda entrada de `editParticipantIds` é
            // adicionada junto com o seed correspondente em `currentUserSeeds`
            // (ver `seedParticipantIds`/`onParticipantsSelected`). Se cair
            // aqui, o participante seria salvo com nome vazio silenciosamente.
            console.warn(`[ticket-info-panel] participante sem seed encontrado: ${id}`);
          }
          return {
            id,
            name: user?.name || '',
          };
        });
        return { participants: participantsData };
      }
      default:
        return {};
    }
  }

  /**
   * O modal de seleção foi fechado sem escolha: no modo campo a campo ele foi aberto
   * automaticamente pelo clique no label, então sair dele encerra também a edição do campo.
   */
  onPickerClosed(): void {
    if (!this.bulkEditMode()) {
      this.cancelEdit();
    }
  }

  // --- Modo de edição em lote ("Modo Edição") ---

  /** Chamado externamente (via @ViewChild) pelo switch "Modo Edição" da barra de ações. */
  enterBulkEditMode(): void {
    if (!this.canEdit) return;
    this.editingField.set(null);
    this.bulkEditFields.forEach(field => this.seedField(field));
    this.bulkEditSnapshot = Object.fromEntries(
      this.bulkEditFields.map(field => [field, this.cloneForSnapshot(this.getEditValue(field))])
    );
    this.bulkEditMode.set(true);
    this.bulkEditModeChanged.emit(true);
  }

  /** Sai do modo lote; se houver alterações não salvas, confirma o descarte antes. */
  requestCancelBulkEdit(): void {
    const changed = this.changedFieldsCount();
    if (changed === 0) {
      this.cancelBulkEdit();
      return;
    }

    this.confirmationService.confirm({
      header: 'Descartar alterações',
      message:
        `Há ${changed} campo${changed === 1 ? '' : 's'} com alterações não salvas. ` +
        '<br>Sair do modo edição vai descartá-las. Continuar?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim, descartar',
      rejectLabel: 'Cancelar, continuar edição',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.cancelBulkEdit(),
    });
  }

  cancelBulkEdit(): void {
    this.bulkEditMode.set(false);
    this.bulkEditModeChanged.emit(false);
  }

  /** Quantos campos têm valor diferente do snapshot tirado ao abrir o lote. */
  changedFieldsCount(): number {
    return this.computeChangedFields().length;
  }

  private computeChangedFields(): string[] {
    return this.bulkEditFields.filter(
      field =>
        !this.areFieldValuesEqual(field, this.getEditValue(field), this.bulkEditSnapshot[field])
    );
  }

  private areFieldValuesEqual(field: string, a: unknown, b: unknown): boolean {
    switch (field) {
      case 'participant_ids':
      case 'ticket_tags':
        return this.sortedValues(a).join(' ') === this.sortedValues(b).join(' ');
      case 'analysis_expected_date':
      case 'development_expected_date':
      case 'expected_date':
        return this.formatDateForAPI(a as Date | null) === this.formatDateForAPI(b as Date | null);
      default:
        return a === b;
    }
  }

  /** Comparação de arrays por conteúdo, ordem-insensível (participantes/tags). */
  private sortedValues(value: unknown): string[] {
    return Array.isArray(value) ? [...value].map(v => String(v)).sort() : [];
  }

  private cloneForSnapshot(value: unknown): unknown {
    if (Array.isArray(value)) return [...value];
    if (value instanceof Date) return new Date(value.getTime());
    return value;
  }

  /**
   * Mesma checagem de `persistField`, mas varrendo o valor `edit*` ATUAL de
   * cada campo obrigatório — em modo lote qualquer um deles pode ter sido
   * esvaziado mesmo sem ter sido o campo "alterado" que o usuário pretendia.
   */
  private validateRequiredFieldsForBulk(): boolean {
    for (const field of this.requiredFields) {
      const value = this.getEditValue(field);
      if (!value || (typeof value === 'string' && !value.trim())) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Campo obrigatório',
          detail:
            'Existe um campo obrigatório vazio (Cliente, Sistema, Solicitante, Responsável ou Status).',
          life: 3500,
        });
        return false;
      }
    }
    return true;
  }

  applyBulkEdit(): void {
    const changed = this.computeChangedFields();
    if (changed.length === 0) {
      this.cancelBulkEdit();
      return;
    }
    if (!this.validateRequiredFieldsForBulk()) return;

    if (
      changed.includes('type') &&
      this.bulkEditSnapshot['type'] === 'L' &&
      this.editType !== 'L'
    ) {
      this.confirmationService.confirm({
        header: 'Remover Liberação Pendente',
        message:
          'Tem certeza que deseja remover o tipo "Liberação pendente" deste chamado? ' +
          'Isso irá liberar o chamado para ser finalizado sem o aval do responsável.',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Sim, Remover',
        rejectLabel: 'Cancelar',
        acceptButtonStyleClass: 'p-button-danger',
        // Ao cancelar, o lote inteiro fica pendente — nada é salvo parcialmente
        // sem o usuário perceber (mantém a promessa de "um clique, um PATCH").
        accept: () => this.persistBulkEdit(changed),
      });
      return;
    }

    this.persistBulkEdit(changed);
  }

  private persistBulkEdit(changedFields: string[]): void {
    const payload: Record<string, unknown> = {};
    changedFields.forEach(field => {
      const fieldPayload = this.buildFieldPayload(field);
      if (Object.keys(fieldPayload).length === 0) {
        // `buildFieldPayload` não tem case pra esse campo — cai no
        // `default: return {}`. Não deveria acontecer (todo campo de
        // `bulkEditFields` tem case tanto aqui quanto em `getEditValue`),
        // mas se acontecer é melhor avisar no console do que deixar o
        // toast de sucesso mentir sobre um campo que não foi enviado.
        console.warn(
          `[ticket-info-panel] buildFieldPayload sem case para "${field}" — campo não será incluído no PATCH.`
        );
      }
      Object.assign(payload, fieldPayload);
    });

    // Mesmo motivo do `persistField`: o snapshot tem de sair antes do `Object.assign`.
    const automationBefore = this.automation.snapshot(this.ticket);

    this.saving.set(true);
    this.ticketService.patchTicket(this.ticket.id, payload).subscribe({
      next: updatedModel => {
        Object.assign(this.ticket, updatedModel);
        this.ticketUpdated.emit(this.ticket);
        this.saving.set(false);
        this.bulkEditMode.set(false);
        this.bulkEditModeChanged.emit(false);
        this.cdr.markForCheck();
        this.messageService.add({
          severity: 'success',
          summary: 'Salvo',
          detail: `${changedFields.length} campo${changedFields.length === 1 ? '' : 's'} atualizado${changedFields.length === 1 ? '' : 's'}`,
          life: 2500,
        });

        // Um único modal de sugestão para o lote todo, mesmo que vários campos tenham mudado.
        this.automation.evaluate(automationBefore, this.ticket);
      },
      error: err => {
        console.error('[ticket-info-panel] Erro ao salvar edição em lote:', err, { payload });
        this.saving.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao salvar alterações',
          life: 4000,
        });
      },
    });
  }

  // --- EntityPicker: campos com busca remota migrados para o seletor rico ---

  readonly searchCustomersFn = (params: EntityPickerSearchParams): Promise<EntityPickerResult> =>
    firstValueFrom(this.ticketService.searchCustomers(params.query || '')).then(items => ({
      items,
      total: items.length,
    }));

  readonly searchSystemsFn = (params: EntityPickerSearchParams): Promise<EntityPickerResult> =>
    firstValueFrom(this.ticketService.searchSystems(params.query || '')).then(items => ({
      items,
      total: items.length,
    }));

  readonly searchUsersFn = (params: EntityPickerSearchParams): Promise<EntityPickerResult> =>
    firstValueFrom(this.ticketService.searchUsers(params.query || '')).then(items => ({
      items,
      total: items.length,
    }));

  readonly searchTicketStatusesFn = (
    params: EntityPickerSearchParams
  ): Promise<EntityPickerResult> =>
    firstValueFrom(this.ticketService.searchTicketStatuses(params.query || '')).then(items => ({
      items,
      total: items.length,
    }));

  readonly searchTicketTypesFn = (params: EntityPickerSearchParams): Promise<EntityPickerResult> =>
    firstValueFrom(this.ticketService.searchTicketTypes(params.query || '')).then(items => ({
      items,
      total: items.length,
    }));

  readonly searchIssuesFn = (params: EntityPickerSearchParams): Promise<EntityPickerResult> =>
    firstValueFrom(this.ticketService.searchIssues(params.query || '')).then(items => ({
      items,
      total: items.length,
    }));

  onCustomerSelected(entity: any): void {
    this.editCustomerId = entity?.id ?? '';
    this.currentCustomerSeed = entity ?? null;
  }

  onSystemSelected(entity: any): void {
    this.editSystemId = entity?.id ?? '';
    this.currentSystemSeed = entity ?? null;
  }

  onResponsibleSelected(entity: any): void {
    this.editResponsibleId = entity?.id ?? '';
    if (entity?.id) this.currentUserSeeds.set(entity.id, entity);
  }

  onDeveloperSelected(entity: any): void {
    this.editDeveloperId = entity?.id ?? '';
    if (entity?.id) this.currentUserSeeds.set(entity.id, entity);
  }

  onExecutorSelected(entity: any): void {
    this.editExecutorId = entity?.id ?? '';
    if (entity?.id) this.currentUserSeeds.set(entity.id, entity);
  }

  onTicketStatusSelected(entity: any): void {
    this.editStatusId = entity?.id ?? '';
    this.currentStatusSeed = entity ?? null;
  }

  onTicketTypeSelected(entity: any): void {
    this.editTicketTypeId = entity?.id ?? '';
    this.currentTicketTypeSeed = entity ?? null;
  }

  onIssueSelected(entity: any): void {
    this.editIssueId = entity?.id ?? '';
    this.currentIssueSeed = entity ?? null;
  }

  /**
   * Participantes usa o EntityPicker em modo múltiplo — cada entidade
   * escolhida alimenta `currentUserSeeds` (não só `usersList`), para
   * `buildFieldPayload('participant_ids')` resolver o nome correto mesmo para
   * quem nunca passou pela busca remota antiga.
   */
  onParticipantsSelected(entities: any[] | null): void {
    const list = entities ?? [];
    this.editParticipantIds = list.map(e => e?.id).filter((id): id is string => !!id);
    list.forEach(e => {
      if (e?.id) this.currentUserSeeds.set(e.id, e);
    });
    this.usersList.set([...this.currentUserSeeds.values()]);
  }

  /** Entidades atualmente selecionadas em Participantes, para o `[value]` do EntityPicker. */
  participantEntities(): any[] {
    return this.editParticipantIds
      .map(id => this.currentUserSeeds.get(id))
      .filter((entity): entity is any => !!entity);
  }

  toggleUrgent(): void {
    this.saving.set(true);
    const newUrgent = this.editUrgent ? 1 : 0;
    this.ticketService.patchTicket(this.ticket.id, { urgent: newUrgent }).subscribe({
      next: updatedModel => {
        Object.assign(this.ticket, updatedModel);
        this.saving.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Salvo',
          detail: 'Urgência atualizada',
          life: 2000,
        });
        this.ticketUpdated.emit(this.ticket);
        this.cdr.markForCheck();
      },
      error: () => {
        this.editUrgent = this.ticket.urgent === 1;
        this.saving.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao atualizar urgência',
          life: 4000,
        });
      },
    });
  }

  get isTypePending(): boolean {
    return this.ticket.type === 'L';
  }

  get isReleaseSituationPending(): boolean {
    return this.ticket.release_situation === 'P';
  }

  get isCloseBlocked(): boolean {
    return this.isTypePending || this.isReleaseSituationPending;
  }

  get closeBlockedTooltip(): string | undefined {
    const messages: string[] = [];
    if (this.isTypePending) {
      messages.push('Chamado com liberação pendente entre em contato com o responsável.');
    }
    if (this.isReleaseSituationPending) {
      messages.push(
        'Chamado com liberação de release pendente. Entre em contato com o desenvolvimento responsável.'
      );
    }
    return messages.length ? messages.join(' ') : undefined;
  }

  closeTicket(): void {
    if (this.isCloseBlocked) {
      return;
    }
    this.saving.set(true);
    this.ticketService.patchTicket(this.ticket.id, { situation: 'F' }).subscribe({
      next: updatedModel => {
        Object.assign(this.ticket, updatedModel);
        this.saving.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Chamado Fechado',
          detail: 'Chamado fechado com sucesso',
          life: 2000,
        });
        this.ticketUpdated.emit(this.ticket);
        this.cdr.markForCheck();
      },
      error: () => {
        this.saving.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao fechar chamado',
          life: 4000,
        });
      },
    });
  }

  reopenTicket(): void {
    this.saving.set(true);
    this.ticketService.patchTicket(this.ticket.id, { situation: 'A' }).subscribe({
      next: updatedModel => {
        Object.assign(this.ticket, updatedModel);
        this.saving.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Chamado Reaberto',
          detail: 'Chamado reaberto com sucesso',
          life: 2000,
        });
        this.ticketUpdated.emit(this.ticket);
        this.cdr.markForCheck();
      },
      error: () => {
        this.saving.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao reabrir chamado',
          life: 4000,
        });
      },
    });
  }

  private applyLocal(field: string): void {
    switch (field) {
      case 'priority':
        this.ticket.priority = this.editPriority;
        break;
      case 'expected_date':
        this.ticket.expected_date = this.formatDateForAPI(this.editExpectedDate);
        break;
      case 'urgent':
        this.ticket.urgent = this.editUrgent ? 1 : 0;
        break;
      case 'requester':
        this.ticket.requester = this.editRequester;
        break;
      case 'customer_id': {
        const selected = this.customersList().find(c => c.id === this.editCustomerId);
        if (selected) {
          this.ticket.customer_name = selected.name;
          this.ticket.customer = { id: selected.id, name: selected.name };
        } else {
          this.ticket.customer_name = '';
          this.ticket.customer = null;
        }
        break;
      }
      case 'system_id': {
        const selectedSys = this.systemsList().find(s => s.id === this.editSystemId);
        if (selectedSys) {
          this.ticket.system = { id: this.editSystemId, description: selectedSys.description };
        }
        break;
      }
      case 'responsible_id': {
        if (!this.editResponsibleId) {
          this.ticket.responsible = null;
        } else {
          const selectedResp = this.usersList().find(u => u.id === this.editResponsibleId);
          if (selectedResp) {
            this.ticket.responsible = { id: this.editResponsibleId, name: selectedResp.name };
          }
        }
        break;
      }
      case 'developer_id': {
        if (!this.editDeveloperId) {
          this.ticket.developer = null;
        } else {
          const selectedDev = this.usersList().find(u => u.id === this.editDeveloperId);
          if (selectedDev) {
            this.ticket.developer = { id: this.editDeveloperId, name: selectedDev.name };
          }
        }
        break;
      }
      case 'ticket_status_id': {
        const selectedStatus = this.statusesList().find(s => s.id === this.editStatusId);
        if (selectedStatus) {
          this.ticket.status = { id: this.editStatusId, description: selectedStatus.description };
        }
        break;
      }
      case 'ticket_type_id': {
        const selectedType = this.ticketTypesList().find(t => t.id === this.editTicketTypeId);
        if (selectedType) {
          this.ticket.ticket_type = {
            id: this.editTicketTypeId,
            description: selectedType.description,
          };
        }
        break;
      }
      case 'estimated_effort':
        this.ticket.estimated_effort = this.editEstimatedEffort;
        break;
      case 'commercial_estimated_effort':
        this.ticket.commercial_estimated_effort = this.editCommercialEstimatedEffort;
        break;
      case 'project_id': {
        const selectedProj = this.projectsList().find(p => p.id === this.editProjectId);
        this.ticket.project = selectedProj
          ? { id: this.editProjectId, description: selectedProj.description }
          : null;
        break;
      }
      case 'phase_id': {
        const selectedPhase = this.phasesList().find(p => p.id === this.editPhaseId);
        this.ticket.phase = selectedPhase
          ? { id: this.editPhaseId, description: selectedPhase.description }
          : null;
        break;
      }
      case 'sprint_id': {
        const selectedSprint = this.sprintsList().find(s => s.id === this.editSprintId);
        this.ticket.sprint = selectedSprint
          ? { id: this.editSprintId, description: selectedSprint.description }
          : null;
        break;
      }
      case 'executor_id': {
        if (!this.editExecutorId) {
          this.ticket.executor = null;
        } else {
          const selectedExec = this.usersList().find(u => u.id === this.editExecutorId);
          if (selectedExec) {
            this.ticket.executor = { id: this.editExecutorId, name: selectedExec.name };
          }
        }
        break;
      }
      case 'issue_id': {
        const selectedIssue = this.issuesList().find(i => i.id === this.editIssueId);
        this.ticket.issue = selectedIssue
          ? { id: this.editIssueId, ref: selectedIssue.ref, description: selectedIssue.description }
          : null;
        break;
      }
      case 'analysis_expected_date':
        this.ticket.analysis_expected_date = this.formatDateForAPI(this.editAnalysisExpectedDate);
        break;
      case 'development_expected_date':
        this.ticket.development_expected_date = this.formatDateForAPI(
          this.editDevelopmentExpectedDate
        );
        break;
      case 'type':
        this.ticket.type = this.editType;
        break;
      case 'email':
        this.ticket.email = this.editEmail;
        break;
      case 'phone':
        this.ticket.phone = this.editPhone;
        break;
      case 'ticket_tags':
        this.ticket.ticket_tags = this.editTags.map(tagName => ({
          id: '',
          tag: { id: '', name: tagName },
        }));
        break;
    }
  }

  formatDate(dateStr: string | null): string {
    if (!dateStr) return '—';
    const d = this.parseDate(dateStr)!;
    return (
      d.toLocaleDateString('pt-BR') +
      ' ' +
      d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    );
  }

  formatDateShort(dateStr: string | null): string {
    if (!dateStr) return '—';
    const d = this.parseDate(dateStr)!;
    return d.toLocaleDateString('pt-BR');
  }

  getParticipantsDisplay(): string {
    if (!this.ticket.ticketUsers?.length) {
      return '—';
    }
    return this.ticket.ticketUsers
      .filter(tu => tu.user_type === 'P')
      .map(tu => tu.user?.name || '')
      .filter(name => name)
      .join(', ');
  }

  getSituationDescription(): string {
    switch (this.ticket.situation) {
      case 'A':
        return 'Aberto';
      case 'F':
        return 'Fechado';
      case 'C':
        return 'Cancelado';
      default:
        return this.ticket.situation || '—';
    }
  }

  getSituationClass(): string {
    switch (this.ticket.situation) {
      case 'A':
        return 'situation-open';
      case 'F':
        return 'situation-closed';
      case 'C':
        return 'situation-cancelled';
      default:
        return '';
    }
  }

  stripCountryCode(phone: string): string {
    const digits = phone.replace(/\D/g, '');
    return digits.length > 11 && digits.startsWith('55') ? digits.slice(2) : digits;
  }
}
